import { gql } from '@apollo/client';

export const GET_EVENT_LOGS_QUERY = gql`
  fragment BaseOrganizationFragment on BaseOrganization {
    id
    name
  }

  fragment EventLogActorFragment on EventLogActor {
    id
    firstName
    lastName
    organization {
      ...BaseOrganizationFragment
    }
  }

  fragment EventLogBaseEventFragment on EventLogBaseEvent {
    eventId
    eventLogType
    eventMessage
    entityId
    createdAt
    updatedAt
    actor {
      ...EventLogActorFragment
    }
  }

  fragment CreditFacilityEntityMetaFragment on EventLogCreditFacility {
    id
    name
    externalFacilityId
  }

  fragment ThreadCreatedEventSnapshotFragment on ThreadCreatedEventSnapshot {
    id
    pinned
    resolved
  }

  # Deliverables
  fragment DeliverableEntityMetaFragment on DeliverableEntityMeta {
    deliverableId: id
    configName
    dueDate
    creditFacilityId
    creditFacilityName
    latestReceiptId
  }
  fragment DeliverableThreadCreateEventFragment on DeliverableThreadCreateEvent {
    ...EventLogBaseEventFragment
    current {
      ...ThreadCreatedEventSnapshotFragment
    }
    entityMetadata {
      ...DeliverableEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
  }

  # Funding Requests
  fragment FundingRequestEntityMetaFragment on FundingRequestEntityMeta {
    fundingRequestId: id
    expire
    status
    creditFacilityId
    creditFacilityName
    loanNames
    externalLoanIds
  }
  fragment FundingRequestStatusEventsCommonSnapshotFragment on FundingRequestStatusEventsCommonSnapshot {
    status
    creditFacilityId
    drawRequestTotalAmount
    collectionsPaydownTotalAmount
    collectionsRecycleTotalAmount
    curePaydownTotalAmount
  }
  fragment FundingRequestThreadCreateEventFragment on FundingRequestThreadCreateEvent {
    ...EventLogBaseEventFragment
    current {
      ...ThreadCreatedEventSnapshotFragment
    }
    entityMetadata {
      ...FundingRequestEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
  }
  fragment FundingRequestCreatedEventFragment on FundingRequestCreatedEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...FundingRequestEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
    current {
      ...FundingRequestStatusEventsCommonSnapshotFragment
    }
  }
  fragment FundingRequestStatusChangeEventFragment on FundingRequestStatusChangeEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...FundingRequestEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
    current {
      ...FundingRequestStatusEventsCommonSnapshotFragment
    }
    previous {
      ...FundingRequestStatusEventsCommonSnapshotFragment
    }
  }

  # Invoices
  fragment InvoiceEntityMetaFragment on InvoiceEntityMeta {
    invoiceId: id
    dueDate
    invoiceStatus: status
    totalAmount
  }
  fragment InvoiceStatusEventsCommonSnapshotFragment on InvoiceStatusEventsCommonSnapshot {
    dueDate
    invoiceStatus: status
    totalAmount
  }

  fragment InvoiceThreadCreateEventFragment on InvoiceThreadCreateEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...InvoiceEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
    current {
      ...ThreadCreatedEventSnapshotFragment
    }
  }
  fragment InvoiceCreatedEventFragment on InvoiceCreatedEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...InvoiceEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
    current {
      ...InvoiceStatusEventsCommonSnapshotFragment
    }
  }
  fragment InvoiceUpdatedEventFragment on InvoiceUpdatedEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...InvoiceEntityMetaFragment
    }
    creditFacility {
      ...CreditFacilityEntityMetaFragment
    }
    current {
      ...InvoiceStatusEventsCommonSnapshotFragment
    }
    previous {
      ...InvoiceStatusEventsCommonSnapshotFragment
    }
  }

  # Limited Partners
  fragment LimitedPartnerEntityMetaFragment on LimitedPartnerEntityMeta {
    limitedPartnerId: id
    name
  }
  fragment LimitedPartnerStatusEventsCommonSnapshotFragment on LimitedPartnerStatusEventsCommonSnapshot {
    id
    name
    otherNames
    ownerOrganizationId
    parentEntityId
  }
  fragment LimitedPartnerMatchEventsCommonSnapshotFragment on LimitedPartnerMatchEventsCommonSnapshot {
    matchMetadataId
    mappingId
    limitedPartnerId
  }

  fragment LimitedPartnerThreadCreateEventFragment on LimitedPartnerThreadCreateEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...LimitedPartnerEntityMetaFragment
    }
    current {
      ...ThreadCreatedEventSnapshotFragment
    }
  }
  fragment LimitedPartnerCreatedEventFragment on LimitedPartnerCreatedEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...LimitedPartnerEntityMetaFragment
    }
    current {
      ...LimitedPartnerStatusEventsCommonSnapshotFragment
    }
  }

  fragment LimitedPartnerUpdatedEventFragment on LimitedPartnerUpdatedEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...LimitedPartnerEntityMetaFragment
    }
    current {
      ...LimitedPartnerStatusEventsCommonSnapshotFragment
    }
    previous {
      ...LimitedPartnerStatusEventsCommonSnapshotFragment
    }
  }

  fragment LimitedPartnerMappingMatchEventFragment on LimitedPartnerMappingMatchEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...LimitedPartnerEntityMetaFragment
    }
    current {
      ...LimitedPartnerMatchEventsCommonSnapshotFragment
    }
  }
  fragment LimitedPartnerMappingUnmatchEventFragment on LimitedPartnerMappingUnmatchEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...LimitedPartnerEntityMetaFragment
    }
    previous {
      ...LimitedPartnerMatchEventsCommonSnapshotFragment
    }
  }

  # Valley Bank
  fragment ValleyBankLoanAccountEntityMetaFragment on ValleySBLLoanAccountEntityMeta {
    loanAccountId: id
  }
  fragment ValleyBankLoanAccountThreadCreateEventFragment on ValleyBankLoanAccountThreadCreateEvent {
    ...EventLogBaseEventFragment
    entityMetadata {
      ...ValleyBankLoanAccountEntityMetaFragment
    }
    current {
      ...ThreadCreatedEventSnapshotFragment
    }
  }

  query GetEventLogs($params: EventLogPaginatedFeedParams!) {
    eventLogs(params: $params) {
      hasMore
      items {
        ...EventLogBaseEventFragment
        ...DeliverableThreadCreateEventFragment
        ...FundingRequestThreadCreateEventFragment
        ...FundingRequestCreatedEventFragment
        ...FundingRequestStatusChangeEventFragment
        ...InvoiceThreadCreateEventFragment
        ...InvoiceCreatedEventFragment
        ...InvoiceUpdatedEventFragment
        ...LimitedPartnerThreadCreateEventFragment
        ...LimitedPartnerCreatedEventFragment
        ...LimitedPartnerUpdatedEventFragment
        ...LimitedPartnerMappingMatchEventFragment
        ...LimitedPartnerMappingUnmatchEventFragment
        ...ValleyBankLoanAccountThreadCreateEventFragment
      }
    }
  }
`;
